/**
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business SLO の目標営業日を説明するポップオーバー。種別 × 緊急度の目標一覧（ops-core の閾値から導出）、営業日の数え方（土日除外・祝日は含む）、オペ担当が 1 名で優先順位を判断している旨を出す。キューの SLO 列見出しと依頼詳細の SLA カードから開く
 * @graph-connects OPS_SLO_TARGET_ROWS [references] 表示する目標営業日の一覧を閾値の SoT から取得
 * @graph-connects OPS_SLA_EVENING_CUTOFF_HOUR_JST [references] 翌日扱いになる起票時刻（説明文に埋める）
 * @graph-connects useFloatingPosition [calls] ポップオーバーの表示位置を算出し画面下端で上へ反転させる
 * @graph-connects opsCategoryLabel [calls] 種別を現在の言語で表示
 */
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { OPS_SLA_EVENING_CUTOFF_HOUR_JST, OPS_SLO_TARGET_ROWS } from '@cortex/ops-core';
import { useFloatingPosition } from '@cortex/web-design-system/hooks/useFloatingPosition';
import { opsCategoryLabel } from './OpsPrimitives.js';

/**
 * 実寸を測れないときに使うパネル幅 (px)。`w-80` 相当。
 *
 * 通常は mount 後に `offsetWidth` を測って使う。`w-80` は 20rem なので、ルートのフォントサイズが
 * 既定の 16px でない環境では 320px にならず、定数で clamp すると画面右端で切れる。実寸が取れない
 * 環境（レイアウトを持たない jsdom 等）だけこの値に落とす。
 * @graph-connects none
 */
const PANEL_WIDTH_FALLBACK = 320;

/** 画面端との最小マージン (px)。 @graph-connects none */
const VIEWPORT_MARGIN = 8;

/**
 * 目標営業日を表示文字列にする。0 営業日は「3営業日」と同じ体裁だと当日締めだと伝わらないので
 * 「当日」と言い切る。
 * @graph-connects none
 */
function targetLabel(days: number, t: TFunction): string {
  return days === 0 ? t('ops.slo.sameDay') : t('ops.slo.businessDays', { count: days });
}

/**
 * SLO の目標営業日を説明するポップオーバー。? ボタンで開閉する。
 *
 * キューの SLO 列見出しは**クリックすると並び替わる**ボタンなので、見出し自体を開閉のトリガーには
 * できない。独立した ? ボタンを隣に置き、並び替えと説明の導線を分ける。
 *
 * パネルは **document.body へ portal する**。`position: fixed` は transform を持つ祖先があると
 * その祖先が包含ブロックになり、viewport 基準で計算した座標が別の原点で解釈されて画面外へ飛ぶ。
 * 依頼詳細は `animate-content-reveal`（transform を使うアニメーション）の内側にあるため、portal
 * しないと「ボタンは反応しているのにパネルが見えない」状態になる。Picker / MultiPicker / Modal も
 * 同じ理由で portal している。
 *
 * portal するとパネルは `<body>` の末尾＝読み上げ順でトリガーから遠く離れるため、開いたら
 * **パネルへ焦点を移す**（移さないとスクリーンリーダー利用者は「展開した」と聞こえた後で中身に
 * 辿り着けず、説明が丸ごと届かない）。Escape で閉じたときはトリガーへ焦点を戻す。
 * @graph-connects none
 */
export function OpsSloExplainer(): ReactNode {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [panelWidth, setPanelWidth] = useState<number | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const rect = useFloatingPosition(triggerRef, open, popoverRef);
  const titleId = useId();
  const panelId = useId();

  /** 閉じてトリガーへ焦点を戻す（キーボード操作の戻り先を失わせない）。 @graph-connects none */
  const closeAndRestoreFocus = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  // 実寸の幅を測る。定数で clamp するとルートのフォントサイズ次第で右端が切れる（w-80 は rem）。
  useLayoutEffect(() => {
    if (!open) {
      setPanelWidth(null);
      return;
    }
    setPanelWidth(popoverRef.current?.offsetWidth ?? null);
  }, [open]);

  // 開いたらパネルへ焦点を移す。portal で DOM 上はトリガーから離れた位置にあるため、
  // 焦点を送らないとキーボード / スクリーンリーダーから中身へ到達できない。
  useEffect(() => {
    if (!open) return;
    popoverRef.current?.focus();
  }, [open]);

  // Escape と外側クリックで閉じる。パネルは portal + fixed で表の外に出るため、閉じる導線が
  // ないとスクロールしても居座り続ける。portal 先でも ref は実ノードを指すので contains は効く。
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') closeAndRestoreFocus();
    };
    // 外側クリックでは焦点を戻さない（利用者が意図して別の場所を押しているため、
    // そこから焦点を奪うと操作の邪魔になる）。
    const onPointerDown = (e: MouseEvent): void => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) === true) return;
      if (popoverRef.current?.contains(target) === true) return;
      setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open, closeAndRestoreFocus]);

  // useFloatingPosition の `width` は trigger（＝小さな ? ボタン）の幅なのでここでは使わない。
  // 代わりに左端を自前で画面内へ収める。SLO 列は表の右寄りにあるため、trigger の left に
  // そのまま合わせるとパネルが画面右外へはみ出す。
  const effectiveWidth = panelWidth !== null && panelWidth > 0 ? panelWidth : PANEL_WIDTH_FALLBACK;
  const left =
    rect === null
      ? 0
      : Math.max(
          VIEWPORT_MARGIN,
          Math.min(rect.left, window.innerWidth - effectiveWidth - VIEWPORT_MARGIN),
        );

  const panel = (
    <div
      ref={popoverRef}
      id={panelId}
      // 開くと焦点を移すので dialog を名乗れる（焦点管理をしないまま dialog を名乗るのは誤り）。
      role="dialog"
      aria-labelledby={titleId}
      // 操作要素を持たないパネル自体を焦点の受け皿にする。これが無いと overflow したときに
      // キーボードだけでスクロールできない。
      tabIndex={-1}
      // rect が出るまでは実高さ / 実幅を測るために mount だけして不可視にする。
      style={
        rect === null
          ? { visibility: 'hidden' }
          : { top: rect.top, left, maxHeight: rect.maxHeight }
      }
      className="fixed z-50 w-80 max-w-[calc(100vw-1rem)] overflow-y-auto rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-elevated)] text-left shadow-xl outline-none"
    >
      <div className="flex items-center gap-2 border-b border-[var(--color-border)] px-4 py-3">
        <span className="h-3.5 w-1 rounded-full bg-[var(--color-accent-primary)]" />
        <span id={titleId} className="text-sm font-semibold text-[var(--color-text-primary)]">
          {t('ops.slo.explainerTitle')}
        </span>
      </div>

      <div className="px-4 py-3">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className="pb-2 text-left text-[10px] font-medium tracking-wider text-[var(--color-text-muted)] uppercase">
                {t('ops.slo.columnCategory')}
              </th>
              <th className="pb-2 text-right text-[10px] font-medium tracking-wider text-[var(--color-text-muted)] uppercase">
                {t('ops.slo.columnNormal')}
              </th>
              <th className="pb-2 text-right text-[10px] font-medium tracking-wider text-[var(--color-text-muted)] uppercase">
                {t('ops.slo.columnUrgent')}
              </th>
            </tr>
          </thead>
          <tbody>
            {OPS_SLO_TARGET_ROWS.map((row) => (
              <tr key={row.category} className="border-t border-[var(--color-border)]">
                <td className="py-2 text-xs text-[var(--color-text-primary)]">
                  {opsCategoryLabel(row.category, t)}
                </td>
                <td className="py-2 text-right text-xs tabular-nums text-[var(--color-text-secondary)]">
                  {targetLabel(row.normalDays, t)}
                </td>
                <td className="py-2 text-right text-xs font-semibold tabular-nums text-[var(--color-accent-primary)]">
                  {targetLabel(row.urgentDays, t)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <p className="mt-3 rounded-lg bg-[var(--color-bg-hover)] px-3 py-2 text-[11px] leading-relaxed text-[var(--color-text-secondary)]">
          {t('ops.slo.businessDayNote', { hour: OPS_SLA_EVENING_CUTOFF_HOUR_JST })}
        </p>
        <p className="mt-3 text-xs leading-relaxed text-[var(--color-text-secondary)]">
          {t('ops.slo.priorityNote')}
        </p>
      </div>
    </div>
  );

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={t('ops.slo.explainerLabel')}
        aria-expanded={open}
        aria-haspopup="dialog"
        // 閉じている間はパネルの id が DOM に存在しないので、参照を張ったままにしない
        // （存在しない id を指す aria-controls は無効な参照になる）。
        {...(open ? { 'aria-controls': panelId } : {})}
        // 既定から色を載せる。グレーの枠線だと列見出しに埋もれて「押せる」と気付かれない
        // （キューが全体的にグレーで迷子になりやすい、という実際のフィードバック）。
        //
        // 塗りつぶし + 黒文字にするのは見た目だけでなくコントラストの都合。淡い地色
        // （warning-muted = 不透明度 8%）にアンバーの文字を置くとライトテーマで 3.2:1 しか出ず、
        // 11px の文字では 4.5:1 に届かない。塗りつぶせばライト 6.6:1 / ダーク 10.4:1 で満たす。
        className={`inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border border-[var(--color-warning)] bg-[var(--color-warning)] text-[11px] leading-none font-bold text-black transition-shadow ${
          open
            ? 'ring-2 ring-[var(--color-warning)] ring-offset-1'
            : 'hover:ring-2 hover:ring-[var(--color-warning)]'
        }`}
        onClick={() => {
          setOpen((v) => !v);
        }}
      >
        ?
      </button>
      {open && createPortal(panel, document.body)}
    </>
  );
}
